import { callTool } from "../api";
import { resolveSharedBoothGroup } from "./sharedBoothGroup";
import type { CompanyBrowserTarget, CompanyDossier } from "./types";

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

/** Overlay mkt-intel account_get / research_account_get profile onto the company dossier. */
export function mergeIntelAccountProfile(dossier: CompanyDossier, intel: unknown): CompanyDossier {
  const payload = asRecord(intel);
  if (!payload) return dossier;
  const account = asRecord(payload.account);
  const incoming = {
    ...(asRecord(payload.profile) ?? {}),
    ...(asRecord(account?.profile) ?? {}),
  };
  const strategic = account && Object.prototype.hasOwnProperty.call(account, "strategic_priorities")
    ? account.strategic_priorities
    : Object.prototype.hasOwnProperty.call(payload, "strategic_priorities")
      ? payload.strategic_priorities
      : undefined;
  if (!Object.keys(incoming).length && strategic === undefined) return dossier;
  const profile = Object.keys(incoming).length ? { ...dossier.profile, ...incoming } : dossier.profile;
  return {
    ...dossier,
    profile,
    account: dossier.account
      ? {
          ...dossier.account,
          profile,
          ...(strategic !== undefined ? { strategic_priorities: strategic } : {}),
        }
      : dossier.account,
  };
}

async function loadIntelProfile(accountId: string, viewer?: string): Promise<unknown> {
  const args: Record<string, unknown> = { account_id: accountId };
  if (viewer) args.viewer = viewer;
  try {
    return await callTool("account_get", args);
  } catch {
    return callTool("research_account_get", args);
  }
}

export async function loadCompanyDossier(
  eventKey: string,
  target: CompanyBrowserTarget,
  campaignKey?: string,
  viewer?: string,
): Promise<CompanyDossier> {
  const args: Record<string, unknown> = { event_key: eventKey };
  if (target.account_id) args.account_id = target.account_id;
  if (target.exhibitor_id) args.exhibitor_id = target.exhibitor_id;
  if (target.booth_number) args.booth_number = target.booth_number;
  if (campaignKey) args.campaign_key = campaignKey;
  if (viewer) args.viewer = viewer;
  const dossier = await callTool<CompanyDossier>("company_browser_get", args);
  const accountId = dossier.resolution.account_id ?? target.account_id;
  let intel: unknown = null;
  let merged = dossier;
  if (accountId) {
    try {
      intel = await loadIntelProfile(accountId, viewer);
      merged = mergeIntelAccountProfile(dossier, intel);
    } catch {
      intel = null;
    }
  }
  return resolveSharedBoothGroup({
    eventKey,
    dossier: merged,
    intel,
    boothNumber: target.booth_number,
    searchAccounts: (name) =>
      callTool("account_search", { event_key: eventKey, name, limit: 5 }),
    listBoothEvents: (boothNumber) =>
      callTool("account_event_list", { event_key: eventKey, booth_number: boothNumber }),
    getCatalogBooth: (boothNumber) =>
      callTool("booth_get", { event_key: eventKey, booth_number: boothNumber }),
  });
}
